import Check from '../Check';
import Report from '../Report';
import { MessageType } from '../Message';
import { uniq } from 'lodash';
import type { IFSM } from '../../../stores/model/metamodel/fsm';
import type { IEventType } from '../../../stores/model/metamodel/event_type';

/**
 * Check for non-determinism of an FSM
 */
class FSMUsedEventsCheck extends Check<IFSM> {
  static description = 'Check used events in FSM';

  check(): Report {
    this.report = new Report();

    const fsm = this.element;

    // Check if all events are used in the FSM
    const usedMethods = fsm.methods;
    const unusedEvents: IEventType[] = [];
    for (const method of fsm.objectType.methods) {
      if (
        !usedMethods.includes(method) &&
        !(method.provenance === 'INHERITED' && method.specialisation != null)
      ) {
        unusedEvents.push(method.ownerEventType);
      }
    }

    if (unusedEvents.length === 0) {
      this.report.addMessage(
        MessageType.INFORMATION,
        `All events of object ${fsm.objectType.name} are used in FSM ${fsm.name}.`,
        this,
        this.element
      );
    } else {
      this.report.addMessage(
        MessageType.WARNING,
        `Events ${uniq(unusedEvents)
          .map((e) => e.name)
          .join(', ')} of object ${fsm.objectType.name} are not used in FSM ${fsm.name}.`,
        this,
        this.element
      );
    }

    // Check if create methods have initial states as source and end methods have
    // final states as target
    for (const transition of fsm.transitions) {
      const methods = transition.metaMethods;
      for (const method of methods) {
        if (fsm.objectType === method.ownerObjectType) {
          if (method.type === 'CREATE' && transition.sourceState.type !== 'INITIAL_STATE') {
            // create method without initial state as its source
            this.report.addMessage(
              MessageType.ERROR,
              `Event ${method.ownerEventType.name} in FSM ${fsm.name} of object ${fsm.objectType.name} doesn't have a start state as its source.`,
              this,
              this.element
            );
          } else if (method.type === 'END' && transition.targetState.type !== 'FINAL_STATE') {
            // end method without final state as its target
            this.report.addMessage(
              MessageType.ERROR,
              `Event ${method.ownerEventType.name} in FSM ${fsm.name} of object ${fsm.objectType.name} doesn't have a final state as its target.`,
              this,
              this.element
            );
          }
        }
      }
    }

    return this.report;
  }
}

export default FSMUsedEventsCheck;
